import { Link } from "react-router-dom";
import { motion, useMotionTemplate, useMotionValue } from "framer-motion";
import { GraduationCap, Search, Scale, ArrowLeft, ArrowRight, FileCheck, Users, School, FileText, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/contexts/LanguageContext";

const serviceIcons = [GraduationCap, Search, Scale, FileCheck, Users, School, FileText, AlertTriangle];
const serviceKeys = ['trainings', 'riskSurveys', 'consulting', 'safetyPlan', 'externalOfficer', 'educationSafety', 'fieldFile', 'incidentInvestigation'];
const serviceHrefs = ['/trainings', '/consulting', '/consulting', '/consulting', '/services/external-consulting', '/services/education-safety-audits', '/services/field-file', '/services/incident-investigation'];
const serviceGradients = [
  "from-amber-500 to-orange-500",
  "from-blue-500 to-indigo-500",
  "from-emerald-500 to-teal-500",
  "from-violet-500 to-purple-500",
  "from-rose-500 to-pink-500",
  "from-green-500 to-emerald-500",
  "from-yellow-500 to-amber-500",
  "from-red-500 to-rose-500",
];

export function GlassServices() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });
  const { t } = useTranslation('home');
  const { language } = useLanguage();

  const services = serviceKeys.map((key, index) => ({
    icon: serviceIcons[index],
    title: t(`services.items.${key}.title`),
    description: t(`services.items.${key}.description`),
    features: t(`services.items.${key}.features`, { returnObjects: true }) as string[],
    href: serviceHrefs[index],
    gradient: serviceGradients[index],
  }));

  return (
    <section className="section-padding bg-background relative overflow-hidden" id="services">
      {/* Background Pattern */}
      <div className="absolute inset-0 pattern-dots opacity-50" />
      
      <div className="container mx-auto relative z-10">
        {/* Section Header */}
        <motion.div 
          ref={ref}
          className="text-center max-w-3xl mx-auto mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block text-accent font-bold text-sm uppercase tracking-widest mb-4 px-4 py-2 rounded-full bg-accent/10 border border-accent/20">
            {t('services.badge')}
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            {t('services.title')}
            <span className="text-gradient-accent"> {t('services.titleAccent')}</span>
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl leading-relaxed">
            {t('services.subtitle')}
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {services.map((service, index) => (
            <GlassServiceCard key={index} service={service} index={index} isVisible={isVisible} language={language} detailsText={t('services.details')} />
          ))}
        </div>
      </div>
    </section>
  );
}

interface GlassServiceCardProps {
  service: {
    icon: React.ComponentType<{ className?: string }>;
    title: string;
    description: string;
    features: string[];
    href: string;
    gradient: string;
  };
  index: number;
  isVisible: boolean;
  language: string;
  detailsText: string;
}

function GlassServiceCard({ service, index, isVisible, language, detailsText }: GlassServiceCardProps) {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  function handleMouseMove({ currentTarget, clientX, clientY }: MouseEvent) {
    const { left, top } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }

  const ArrowIcon = language === 'he' ? ArrowLeft : ArrowRight;

  return (
    <motion.div
      className="group relative"
      initial={{ opacity: 0, y: 30 }}
      animate={isVisible ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.1, duration: 0.5 }}
      onMouseMove={handleMouseMove}
    >
      <div className="relative h-full rounded-xl border border-border/50 bg-card/80 backdrop-blur-xl overflow-hidden transition-all duration-500 hover:border-accent/30 hover:shadow-lg hover:-translate-y-1">
        {/* Spotlight Effect */}
        <motion.div
          className="pointer-events-none absolute -inset-px rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{
            background: useMotionTemplate`
              radial-gradient(
                300px circle at ${mouseX}px ${mouseY}px,
                hsl(38 92% 50% / 0.1),
                transparent 80%
              )
            `,
          }}
        />

        {/* Gradient Glow */}
        <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl ${service.gradient} opacity-0 group-hover:opacity-20 blur-2xl transition-opacity duration-500`} />

        <div className="relative p-4 pb-5 h-full flex flex-col">
          {/* Icon */}
          <motion.div 
            className={`w-10 h-10 rounded-lg bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-3 shadow-md`}
            whileHover={{ scale: 1.1 }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
          >
            <service.icon className="w-5 h-5 text-white" />
          </motion.div>

          {/* Title */}
          <h3 className="font-heading text-sm font-bold text-foreground mb-2 group-hover:text-accent transition-colors duration-300 line-clamp-1">
            {service.title}
          </h3>

          {/* Description */}
          <p className="text-xs text-muted-foreground leading-relaxed mb-3 line-clamp-2">
            {service.description}
          </p>

          {/* Features - compact */}
          <ul className="space-y-1 mb-3 flex-1">
            {service.features.slice(0, 2).map((feature, fIndex) => (
              <li 
                key={fIndex}
                className="flex items-center gap-1.5 text-xs text-foreground/70"
              >
                <span className="w-1 h-1 rounded-full bg-accent flex-shrink-0" />
                <span className="line-clamp-1">{feature}</span>
              </li>
            ))}
          </ul>

          {/* CTA */}
          <Link to={service.href} className="mt-auto">
            <Button variant="outline" size="sm" className="w-full text-xs min-h-[32px]">
              <ArrowIcon className="w-3 h-3" />
              {detailsText}
            </Button>
          </Link>
        </div>
      </div>
    </motion.div>
  );
}
